import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSchedule } from '../../hooks/useSchedule';
import { useDragDrop } from '../../hooks/useDragDrop';
import { TimeSlot } from './TimeSlot';
import { DAYS, TIME_SLOTS, BROADCAST_TYPES } from '../../utils/constants';
import { formatTime, formatWeekRangeFr, getDayNumber } from '../../utils/helpers';
import * as scheduleService from '../../services/scheduleService';

export function ScheduleGrid() {
  const { canEdit } = useAuth();
  const {
    currentWeek,
    gridData,
    allShows,
    loading,
    fetchWeeks,
    fetchGrid,
    fetchAllShows,
    moveEntry,
    addEntry,
    updateEntry,
    deleteEntry,
  } = useSchedule();
  const [editingEntry, setEditingEntry] = useState(null);
  const [addingSlot, setAddingSlot] = useState(null);

  const { DndContext, dndContextProps, activeId } = useDragDrop({
    onMoveEntry: moveEntry,
    canEdit,
  });

  useEffect(() => {
    fetchWeeks();
  }, [fetchWeeks]);

  useEffect(() => {
    if (canEdit) {
      fetchAllShows();
    }
  }, [canEdit, fetchAllShows]);

  useEffect(() => {
    if (currentWeek?.id) {
      fetchGrid(currentWeek.id);
    }
  }, [currentWeek?.id, fetchGrid]);

  const entries = gridData?.entries ?? [];
  const shows = gridData?.shows ?? [];
  const showsMap = Object.fromEntries((shows || []).map((s) => [s.id, s]));

  const weekLabel = currentWeek?.start_date
    ? formatWeekRangeFr(currentWeek.start_date)
    : '';

  const handleEditEntry = useCallback((entry) => {
    setEditingEntry(entry);
  }, []);

  const handleUpdateEntry = useCallback(
    async (id, data) => {
      await updateEntry(id, data);
      setEditingEntry(null);
    },
    [updateEntry]
  );

  const handleDeleteEntry = useCallback(
    async (id) => {
      await deleteEntry(id);
    },
    [deleteEntry]
  );

  const handleAddEntry = useCallback(
    (dayId, time) => {
      if (!canEdit) return;
      setAddingSlot({ dayId, time });
    },
    [canEdit]
  );

  const handleCreateEntry = useCallback(
    async (data) => {
      if (!currentWeek?.id) return;
      await addEntry({
        week_id: currentWeek.id,
        show_id: data.show_id,
        episode_id: data.episode_id || null,
        day_of_week: data.day_of_week,
        start_time: data.start_time,
        duration: data.duration,
        broadcast_type: data.broadcast_type,
      });
      setAddingSlot(null);
    },
    [currentWeek?.id, addEntry]
  );

  if (loading && !gridData) {
    return (
      <div className="schedule-grid-container">
        <div className="schedule-grid grid-skeleton">
          <div className="grid-row">
            <div className="skeleton-cell" />
            {DAYS.map((d) => (
              <div key={d.id} className="skeleton-cell" />
            ))}
          </div>
          {TIME_SLOTS.map((t) => (
            <div key={t} className="grid-row">
              <div className="skeleton-cell" />
              {DAYS.map((d) => (
                <div key={d.id} className="skeleton-cell" />
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="schedule-grid-container">
      {weekLabel && (
        <div className="grid-week-banner">{weekLabel}</div>
      )}
      <DndContext {...dndContextProps}>
        <div className="schedule-grid-wrapper">
          <div className="schedule-grid">
            <div className="grid-row">
              <div className="grid-corner" />
              {DAYS.map((day) => {
                const dayNum = getDayNumber(currentWeek?.start_date, day.id);
                return (
                  <div key={day.id} className="grid-day-header">
                    <span className="day-name">{day.name}{dayNum ? ` (${dayNum})` : ''}</span>
                  </div>
                );
              })}
            </div>
            {TIME_SLOTS.map((time) => (
              <div key={time} className="grid-row">
                <div className="grid-time-cell">{formatTime(time)}</div>
                {DAYS.map((day) => (
                  <TimeSlot
                    key={`${day.id}-${time}`}
                    dayId={day.id}
                    time={time}
                    entries={entries}
                    showsMap={showsMap}
                    canEdit={canEdit}
                    onEdit={handleEditEntry}
                    onDelete={handleDeleteEntry}
                    onAddEntry={handleAddEntry}
                    activeId={activeId}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </DndContext>

      <div className="grid-legend">
        {BROADCAST_TYPES.filter((type) => type.value !== 'rerun').map((type) => (
          <div key={type.value} className="legend-item">
            <span className="legend-swatch" style={{ backgroundColor: type.color }} />
            <span>{type.label}</span>
          </div>
        ))}
      </div>

      {editingEntry && (
        <EntryEditModal
          entry={editingEntry}
          show={showsMap[editingEntry.show_id]}
          allShows={allShows}
          onSave={(data) => handleUpdateEntry(editingEntry.id, data)}
          onClose={() => setEditingEntry(null)}
        />
      )}

      {addingSlot && (
        <AddEntryModal
          dayId={addingSlot.dayId}
          time={addingSlot.time}
          entries={entries}
          shows={allShows}
          onSave={handleCreateEntry}
          onClose={() => setAddingSlot(null)}
        />
      )}
    </div>
  );
}

function computeAvailableMinutes(dayId, time, entries) {
  const slotIndex = TIME_SLOTS.indexOf(time);
  if (slotIndex < 0) return 30;

  const dayEntries = (entries || [])
    .filter((e) => e.day === dayId)
    .map((e) => ({ ...e, slotIdx: TIME_SLOTS.indexOf(e.time) }))
    .filter((e) => e.slotIdx > slotIndex)
    .sort((a, b) => a.slotIdx - b.slotIdx);

  const nextOnDay = dayEntries.length > 0 ? dayEntries[0].slotIdx : 48;
  let available = nextOnDay - slotIndex;

  if (nextOnDay === 48 && dayId < 7) {
    const nextDayEntries = (entries || [])
      .filter((e) => e.day === dayId + 1)
      .map((e) => ({ ...e, slotIdx: TIME_SLOTS.indexOf(e.time) }))
      .filter((e) => e.slotIdx >= 0)
      .sort((a, b) => a.slotIdx - b.slotIdx);

    const firstNextDay = nextDayEntries.length > 0 ? nextDayEntries[0].slotIdx : 48;
    available += firstNextDay;
  }

  return available * 30;
}

const DURATION_LABELS = {
  30: '30 min',
  60: '1h',
  90: '1h30',
  120: '2h',
  150: '2h30',
  180: '3h',
};

function AddEntryModal({ dayId, time, entries, shows, onSave, onClose }) {
  const [showId, setShowId] = useState('');
  const [episodeId, setEpisodeId] = useState('');
  const [broadcastType, setBroadcastType] = useState('rerun');
  const [saving, setSaving] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);

  const dayName = DAYS.find((d) => d.id === dayId)?.name || '';
  const availableMinutes = computeAvailableMinutes(dayId, time, entries);

  const fittingShows = shows
    .filter((s) => (s.default_duration || 30) <= availableMinutes)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr'));

  const groupedByDuration = {};
  fittingShows.forEach((s) => {
    const dur = s.default_duration || 30;
    if (!groupedByDuration[dur]) groupedByDuration[dur] = [];
    groupedByDuration[dur].push(s);
  });
  const sortedDurations = Object.keys(groupedByDuration).map(Number).sort((a, b) => a - b);

  const selectedShow = shows.find((s) => String(s.id) === showId);
  const selectedDuration = selectedShow?.default_duration || 30;

  useEffect(() => {
    if (!showId) {
      setEpisodes([]);
      setEpisodeId('');
      return;
    }
    let cancelled = false;
    setLoadingEpisodes(true);
    scheduleService.getEpisodes(showId).then((data) => {
      if (!cancelled) {
        setEpisodes(Array.isArray(data) ? data : []);
        setLoadingEpisodes(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setEpisodes([]);
        setLoadingEpisodes(false);
      }
    });
    return () => { cancelled = true; };
  }, [showId]);

  const episodesBySeason = {};
  episodes.forEach((ep) => {
    const s = ep.season_number || 1;
    if (!episodesBySeason[s]) episodesBySeason[s] = [];
    episodesBySeason[s].push(ep);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!showId) return;
    setSaving(true);
    try {
      await onSave({
        show_id: parseInt(showId, 10),
        episode_id: episodeId ? parseInt(episodeId, 10) : null,
        day_of_week: dayId,
        start_time: time + ':00',
        duration: selectedDuration,
        broadcast_type: broadcastType,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-dialog-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            Ajouter — {dayName} {formatTime(time)}
          </h2>
          <span className="modal-subtitle">
            {availableMinutes >= 180
              ? '3h+ disponible'
              : `${DURATION_LABELS[availableMinutes] || availableMinutes + ' min'} disponible`}
          </span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Émission</label>
            {fittingShows.length === 0 ? (
              <p className="form-hint">Aucune émission ne rentre dans ce créneau.</p>
            ) : (
              <select value={showId} onChange={(e) => { setShowId(e.target.value); setEpisodeId(''); }} required>
                <option value="" disabled>— Choisir une émission —</option>
                {sortedDurations.map((dur) => (
                  <optgroup key={dur} label={DURATION_LABELS[dur] || `${dur} min`}>
                    {groupedByDuration[dur].map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>

          {showId && (
            <div className="form-group">
              <label>Épisode</label>
              {loadingEpisodes ? (
                <p className="form-hint">Chargement des épisodes...</p>
              ) : episodes.length === 0 ? (
                <p className="form-hint">Aucun épisode disponible pour cette émission.</p>
              ) : (
                <select value={episodeId} onChange={(e) => setEpisodeId(e.target.value)}>
                  <option value="">— Aucun épisode —</option>
                  {Object.keys(episodesBySeason).sort((a, b) => a - b).map((s) => (
                    <optgroup key={s} label={`Saison ${s}`}>
                      {episodesBySeason[s].map((ep) => (
                        <option key={ep.id} value={ep.id}>
                          E{ep.episode_number} — {ep.title || `Épisode ${ep.episode_number}`}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Type de diffusion</label>
            <select value={broadcastType} onChange={(e) => setBroadcastType(e.target.value)}>
              {BROADCAST_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !showId}>
              {saving ? 'Ajout en cours...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EntryEditModal({ entry, show, allShows, onSave, onClose }) {
  const [episodeId, setEpisodeId] = useState(entry?.episode_id ?? '');
  const [broadcastType, setBroadcastType] = useState(entry?.broadcast_type ?? 'rerun');
  const [duration, setDuration] = useState(entry?.duration ?? 30);
  const [saving, setSaving] = useState(false);
  const [episodes, setEpisodes] = useState([]);

  useEffect(() => {
    if (!entry?.show_id) return;
    scheduleService.getEpisodes(entry.show_id).then((data) => {
      setEpisodes(Array.isArray(data) ? data : []);
    }).catch(() => setEpisodes([]));
  }, [entry?.show_id]);

  const episodesBySeason = {};
  episodes.forEach((ep) => {
    const s = ep.season_number || 1;
    if (!episodesBySeason[s]) episodesBySeason[s] = [];
    episodesBySeason[s].push(ep);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        episode_id: episodeId ? parseInt(episodeId, 10) : null,
        broadcast_type: broadcastType,
        duration,
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-dialog modal-dialog-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Modifier: {show?.name}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Fermer">×</button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Épisode</label>
            {episodes.length === 0 ? (
              <p className="form-hint">Aucun épisode disponible.</p>
            ) : (
              <select value={episodeId || ''} onChange={(e) => setEpisodeId(e.target.value)}>
                <option value="">— Aucun épisode —</option>
                {Object.keys(episodesBySeason).sort((a, b) => a - b).map((s) => (
                  <optgroup key={s} label={`Saison ${s}`}>
                    {episodesBySeason[s].map((ep) => (
                      <option key={ep.id} value={ep.id}>
                        E{ep.episode_number} — {ep.title || `Épisode ${ep.episode_number}`}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>
          <div className="form-group">
            <label>Type de diffusion</label>
            <select value={broadcastType} onChange={(e) => setBroadcastType(e.target.value)}>
              {BROADCAST_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Durée (min)</label>
            <select value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))}>
              {[30, 60, 90, 120, 150, 180].map((d) => (
                <option key={d} value={d}>
                  {d === 30 ? '30 min' : d < 60 ? `${d} min` : `${d / 60}h${d % 60 ? d % 60 : ''}`}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
